import { getPermissionData, getUserAccountList, getUserInfo } from '@/api';
import { getRightsDisplay } from '@rendora/common/entities/rights';
import { usePermissionStore } from '@rendora/common/store/permission';
import { useRightsStore } from '@rendora/common/store/rights';
import { useUserStore } from '@rendora/common/store/user';
import { isPureHost } from '@rendora/common/utils/config';
import type { ServerSeoData } from '~/api/interface';
import { getApplicationSceneTags } from '~/api/module/scenarios';
import { getSeoData } from '~/api/module/seo';
import { getTags } from '~/api/module/tags';
import { CustomSettingApp } from '~/composables/custom-setting';
import { useLicense } from '~/composables/license';
import { useCenterTags, useCharacterTags, useScenariosTags, useTagMap } from '~/composables/tags';
import { PageKey } from '~/constants/page';
import { useSeoData } from '~/hooks/useSeo';
import { setTags } from '~/utils';
import { isChinese } from '~/utils/area';
import { concurrency } from '~/utils/concurrency';

// License URL 配置
const LICENSE_URLS = {
  cn: '//official-site.youyan.xyz/config/license.json',
  us: '//fe.rendora.ai/config/third-license.json',
} as const;

/**
 * 处理异步数据结果
 */
function onSuccess(data: { error?: Ref; data?: Ref } | undefined, cb: () => void) {
  if (!data) {
    return;
  }

  if (data.error?.value) {
    // eslint-disable-next-line no-console
    console.error(data.error.value);
    return;
  }

  if (data.data?.value) {
    cb();
  }
}

/**
 * 转换 SEO 数据格式
 */
function transformSeoData(seoList: ServerSeoData[] = []) {
  return seoList.map(item => ({
    path: `${item.path}`,
    title: item.seo_title,
    description: item.seo_description,
    keywords: item.seo_keywords,
  }));
}

export default defineNuxtPlugin(async () => {
  const route = useRoute();

  const url = useRequestURL();

  // 初始化一下 favicon
  if (isPureHost(url.host) && import.meta.server) {
    useHead({
      link: [
        {
          rel: 'icon',
          type: 'image/x-icon',
          href: getFeUrl('/assets/ico/favicon-default.ico'),
        },
      ],
    });
  }

  // 跳过第三方登录页面
  if (route.name === PageKey.THIRD_AUTH_LOGIN) {
    return;
  }

  // 初始化 composables
  const userInfo = useUserInfo();
  const session = useSession();
  const accountList = useAccountList();
  const seoData = useSeoData();
  const permissionData = usePermissionData();
  const runtimeConfig = useRuntimeConfig();
  const request = useRequestURL();
  const scenariosTags = useScenariosTags();
  const license = useLicense();

  // 构建关键数据的并行请求
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const bootRequestArray: Promise<any>[] = [
    session?.value?.user_id ? useAsyncData(getUserInfo) : Promise.resolve(),
    useAsyncData(getPermissionData),
    !runtimeConfig.public.privatization ? getSeoData() : Promise.resolve([]),
    useAsyncData('ScenariosTags', () => getApplicationSceneTags()),
  ];

  // 并行执行关键数据请求
  const result = await concurrency(bootRequestArray);
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const [userInfoResult, permissionDataResult, seoResult, scenariosTagResult] = result as any[];

  // 处理场景标签数据
  onSuccess(scenariosTagResult, () => {
    if (scenariosTagResult?.data.value?.data) {
      scenariosTags.value = scenariosTagResult.data.value.data;
    }
  });

  // 处理用户信息
  onSuccess(userInfoResult, () => {
    const userData = userInfoResult!.data.value?.data;
    if (userData) {
      userInfo.value = userData;
      if (import.meta.client) {
        const userStore = useUserStore();
        userStore.setUserInfo(userData);
      }
      CustomSettingApp.initAvatar(userData.logo_url || '', userData.default_logo_url || '');
      CustomSettingApp.initLogo(
        userData.platform_logo_url || '',
        userData.default_platform_logo_url || '',
      );
    }
  });

  // 处理权限数据（favicon 由 permissionStore.setPermissionConfig 统一处理）
  onSuccess(permissionDataResult, () => {
    const permissionCfg = permissionDataResult.data.value?.data;
    permissionData.value = permissionCfg;
    if (import.meta.client) {
      const permissionStore = usePermissionStore();
      permissionStore.setPermissionConfig(permissionCfg);
    }
  });

  // 客户端优先使用服务端权限数据（避免重复请求）

  // 处理 SEO 数据
  const seoList = (seoResult || []) as ServerSeoData[];
  if (import.meta.server) {
    // 服务端：只处理当前路径的 SEO 数据
    seoData.value = transformSeoData(seoList.filter(item => item.path === request.pathname));
  } else if (import.meta.client) {
    // 客户端：处理所有 SEO 数据
    seoData.value = transformSeoData(seoList);
  }

  // 非关键数据：客户端延迟加载（优化 TTFB）
  if (import.meta.client) {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const nonCriticalRequests: Promise<any>[] = [
      session?.value?.user_id ? useAsyncData(getUserAccountList) : Promise.resolve(),
      useAsyncData(() => getRightsDisplay()),
      useAsyncData(() => getTags({ group: 'template' })),
      useAsyncData(() => getTags({ group: 'character' })),
      useAsyncData(
        'license',
        () => fetch(isChinese() ? LICENSE_URLS.cn : LICENSE_URLS.us).then(res => res.json()),
        {
          server: false,
          lazy: true,
        },
      ),
    ];

    concurrency(nonCriticalRequests).then(results => {
      const [
        accountListResult,
        rightsDisplayResult,
        templateTagResult,
        characterTagResult,
        licenseResult,
      ] = results as any[];

      // 处理账号列表
      onSuccess(accountListResult, () => {
        accountList.value = accountListResult!.data.value?.data || [];
        if (import.meta.client) {
          const userStore = useUserStore();
          userStore.setAccountList(accountListResult!.data.value?.data || []);
        }
      });

      // 处理 License 数据
      if (licenseResult?.data?.value) {
        license.value = licenseResult.data.value;
      }

      // 处理标签数据
      const map = useTagMap();
      const centerTags = useCenterTags();
      const digitalTags = useCharacterTags();
      const rightsStore = useRightsStore();

      onSuccess(rightsDisplayResult, () => {
        const rightsDisplayData = rightsDisplayResult.data.value?.data;
        if (rightsDisplayData) {
          rightsStore.setRightsDisplay(rightsDisplayData);
        }
      });

      onSuccess(templateTagResult, () => {
        const tagData = templateTagResult.data.value?.data;
        if (tagData) {
          setTags(tagData.tags, undefined, map);
          centerTags.value = tagData;
        }
      });

      onSuccess(characterTagResult, () => {
        if (characterTagResult.data.value?.data) {
          digitalTags.value = characterTagResult.data.value.data;
        }
      });
    });
  }
});
